import {
  AssessmentResultTypeComponent,
  type AssessmentResult,
  type Candidate,
} from '~/types/__generated__'

export function useAssessmentHelper() {
  const assessmentMapper = useAssessmentMapper()
  const { extractMostAccurateCognitiveLevelFromAssessmentResult } =
    useAssessmentCognitive()

  const sortDescByAssessmentScore = (a: any, b: any) => {
    return b.assessmentScore - a.assessmentScore
  }

  const extractTopMotivations = (
    motivationsAssessment: AssessmentResult,
    amount: number,
    offset: number = 0,
  ) => {
    return [
      ...(motivationsAssessment?.data?.find(
        (data: any) => data.name === 'carreerValues',
      )?.subdata || []),
    ]
      .sort(sortDescByAssessmentScore)
      .slice(offset, amount)
  }

  const extractTopCultures = (
    personalityAssessment: AssessmentResult,
    amount: number,
    offset: number = 0,
  ) => {
    return [
      ...(personalityAssessment?.data?.find((data) => data.name === 'culture')
        ?.subdata || []),
    ]
      .sort(sortDescByAssessmentScore)
      .slice(offset, amount)
  }

  const extractMappedTopCultures = (
    personalityAssessment: AssessmentResult,
    amount: number,
    offset: number = 0,
  ) => {
    return extractTopCultures(personalityAssessment, amount, offset).map(
      (culture) => assessmentMapper.mapCulture(culture.name),
    )
  }

  const extractTopOperationalRoles = (
    personalityAssessment: AssessmentResult,
    amount: number,
    offset: any = 0,
  ) => {
    return [
      ...(personalityAssessment?.data?.find(
        (data) => data.name === 'operationalRoles',
      )?.subdata || []),
    ]
      .sort(sortDescByAssessmentScore)
      .slice(offset, amount)
  }

  const extractMappedTopOperationalRoles = (
    personalityAssessment: AssessmentResult,
    amount: number,
    offset: number = 0,
  ) => {
    return extractTopOperationalRoles(
      personalityAssessment,
      amount,
      offset,
    ).map((operationalRole) =>
      assessmentMapper.mapOperationalRole(operationalRole.name),
    )
  }

  function extractMappedTopMotivations(
    motivationsAssessment: AssessmentResult,
    amount: number,
    offset: number = 0,
  ) {
    return extractTopMotivations(motivationsAssessment, amount, offset).map(
      (motivation) => assessmentMapper.mapMotivation(motivation.name),
    )
  }

  function extractMappedHighestType(personalityAssessment: AssessmentResult) {
    const rolesData = personalityAssessment?.data?.find(
      (data) => data.name === 'roles',
    )?.subdata
    if (!rolesData) return null
    const sortedRoles = [...rolesData].sort(sortDescByAssessmentScore)
    const highestRole = sortedRoles[0]
    return assessmentMapper.mapPersonalityType(highestRole?.name)
  }

  function extractMappedDataFromCandidate(candidate: Candidate) {
    const motivationsAssessment = candidate?.assessmentResults?.find(
      (assessmentResult) =>
        assessmentResult?.type?.resultType ===
        AssessmentResultTypeComponent.resultType.MOTIVATIONS,
    )

    const personalityAssessment = candidate?.assessmentResults?.find(
      (assessmentResult) =>
        assessmentResult?.type?.resultType ===
        AssessmentResultTypeComponent.resultType.PERSONALITY,
    )

    const cognitiveAssessment = candidate?.assessmentResults?.find(
      (assessmentResult) =>
        assessmentResult?.type?.resultType ===
        AssessmentResultTypeComponent.resultType.COGNITIVE,
    )

    const mappedTopMotivations = motivationsAssessment
      ? extractMappedTopMotivations(motivationsAssessment, 3)
      : []
    const mappedTopCultures = personalityAssessment
      ? extractMappedTopCultures(personalityAssessment, 3)
      : []
    const mappedTopOperationalRoles = personalityAssessment
      ? extractMappedTopOperationalRoles(personalityAssessment, 3)
      : []
    const cognitiveLevels = cognitiveAssessment
      ? extractMostAccurateCognitiveLevelFromAssessmentResult(
          cognitiveAssessment,
        )
        ? [
            {
              label:
                extractMostAccurateCognitiveLevelFromAssessmentResult(
                  cognitiveAssessment,
                ),
            },
          ]
        : []
      : []

    const mappedHighestType = personalityAssessment
      ? extractMappedHighestType(personalityAssessment)
      : []

    return {
      topMotivations: mappedTopMotivations,
      topCultures: mappedTopCultures,
      topOperationalRoles: mappedTopOperationalRoles,
      cognitiveLevels,
      personalityType: mappedHighestType,
    }
  }

  function sortByAssessmentScore(
    array: { assessmentScore: number }[],
    order: 'asc' | 'desc' = 'desc',
  ) {
    return [...array].sort((a, b) => {
      if (order === 'asc') {
        return a.assessmentScore - b.assessmentScore
      }
      return b.assessmentScore - a.assessmentScore
    })
  }

  function isCanidateAssessmentFinished(
    candidate: Candidate,
    assessmentType: AssessmentResultTypeComponent.resultType,
  ) {
    return !!candidate?.assessments
      ?.find((assessment) =>
        assessment?.providesResultsFor?.some(
          (providedResult) => providedResult.resultType === assessmentType,
        ),
      )
      ?.tasks?.every((task) => task.finished)
  }

  function candidateHasAssessmentResults(
    candidate: Candidate,
    assessmentType: AssessmentResultTypeComponent.resultType,
  ) {
    return !!candidate?.assessmentResults?.find(
      (assessmentResult) =>
        assessmentResult?.type?.resultType === assessmentType &&
        assessmentResult?.data?.length,
    )
  }

  function getAssessmentFromCandidate(
    candidate: Candidate,
    providesResultFor: AssessmentResultTypeComponent.resultType,
  ) {
    return candidate?.assessments?.find((assessment) =>
      assessment?.providesResultsFor?.some(
        (providedResult) => providedResult.resultType === providesResultFor,
      ),
    )
  }

  function getAssessmentUrlFromCandidate(
    candidate: Candidate,
    providesResultFor: AssessmentResultTypeComponent.resultType,
  ) {
    const assessment = getAssessmentFromCandidate(candidate, providesResultFor)
    return assessment?.tasks?.find((task) => !task.finished)?.url
  }

  function getAssessmentReportsFromCandidate(
    candidate: Candidate,
    assessmentType: AssessmentResultTypeComponent.resultType,
  ) {
    const result = candidate?.assessmentResults?.find(
      (assessmentResult) =>
        assessmentResult?.type?.resultType === assessmentType,
    )
    return result?.assessmentReports
  }

  return {
    extractMappedDataFromCandidate,
    extractMappedHighestType,
    extractMappedTopMotivations,
    isCanidateAssessmentFinished,
    candidateHasAssessmentResults,
    getAssessmentFromCandidate,
    getAssessmentUrlFromCandidate,
    getAssessmentReportsFromCandidate,
    sortByAssessmentScore,
  }
}
